import { ChangeEvent, useState, useEffect, Dispatch, SetStateAction } from 'react';
import { FormControl, FormHelperText } from '@mui/material';
import BaseDialog from '@/components/ui/BaseDialog';
import BaseTextField from '@/components/form/BaseTextField';
import { Product } from '@/types/pages/storeManagement/product';
import { useTranslation } from 'react-i18next';
import { validateProductField } from './productValidation';

interface EditProductDialogProps {
    isDialogOpen: boolean;
    editData: Product;
    setEditData: Dispatch<SetStateAction<Product>>;
    onConfirm: () => void;
    onClose: () => void;
}
export default function EditProductDialog({
    isDialogOpen,
    editData,
    setEditData,
    onConfirm,
    onClose,
}: EditProductDialogProps) {
    const { t } = useTranslation('common');
    const [validatedMessage, setValidatedMessage] = useState<{
        [key: string]: string | null;
    }>({});

    const checkIsRequired = (key: string) => {
        return key === 'number' || key === 'barcode' || key === 'name' || key === 'price';
    };

    const getValidationMessage = (key: string, value: unknown) => {
        if (checkIsRequired(key) && (value === '' || value == null)) return 'Required';
        return validateProductField(key, value);
    };

    const inputChangeHandler = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, key: string) => {
        const value = e.target.value;

        setEditData((prevData) => ({
            ...prevData,
            [key]: value ?? '',
        }));

        setValidatedMessage((prevMessage) => ({
            ...prevMessage,
            [key]: getValidationMessage(key, value),
        }));
    };

    const formValidate = () => {
        const newValidatedMessage: { [key: string]: string | null } = {};
        let isFormValid = true;

        (Object.keys(editData) as (keyof Product)[]).forEach((key) => {
            const message = getValidationMessage(key, editData[key]);
            if (message) {
                newValidatedMessage[key] = message;
                isFormValid = false;
            }
        });

        setValidatedMessage(newValidatedMessage);
        return isFormValid;
    };

    const validateBeforeConfirm = () => {
        if (!formValidate()) return;
        onConfirm();
    };

    useEffect(() => {
        if (!isDialogOpen) {
            setValidatedMessage({});
        }
    }, [isDialogOpen]);
    return (
        <BaseDialog
            open={isDialogOpen}
            dialogTitle={t('Edit Product')}
            confirmButtonText={t('Save')}
            cancelButtonText={t('Cancel')}
            onConfirm={validateBeforeConfirm}
            onClose={onClose}
        >
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    columnGap: '1.5rem',
                    rowGap: '0.8rem',
                    marginTop: '0.4rem',
                }}
            >
                <FormControl error={!!validatedMessage['number']}>
                    <BaseTextField
                        label={t('Number')}
                        required={checkIsRequired('number')}
                        error={!!validatedMessage.number}
                        value={editData.number || ''}
                        onChange={(e) => inputChangeHandler(e, 'number')}
                    />
                    <FormHelperText>{validatedMessage.number ? t(validatedMessage.number) : ''}</FormHelperText>
                </FormControl>
                <FormControl error={!!validatedMessage['barcode']}>
                    <BaseTextField
                        label={t('Barcode')}
                        required={checkIsRequired('barcode')}
                        error={!!validatedMessage.barcode}
                        value={editData.barcode || ''}
                        onChange={(e) => inputChangeHandler(e, 'barcode')}
                    />
                    <FormHelperText>{validatedMessage.barcode ? t(validatedMessage.barcode) : ''}</FormHelperText>
                </FormControl>
                <FormControl error={!!validatedMessage['name']}>
                    <BaseTextField
                        label={t('Name')}
                        required={checkIsRequired('name')}
                        error={!!validatedMessage.name}
                        value={editData.name || ''}
                        onChange={(e) => inputChangeHandler(e, 'name')}
                    />
                    <FormHelperText>{validatedMessage.name ? t(validatedMessage.name) : ''}</FormHelperText>
                </FormControl>
                <FormControl error={!!validatedMessage['area']}>
                    <BaseTextField
                        label={t('Area')}
                        required={checkIsRequired('area')}
                        error={!!validatedMessage.area}
                        value={editData.area || ''}
                        onChange={(e) => inputChangeHandler(e, 'area')}
                    />
                    <FormHelperText>{validatedMessage.area ? t(validatedMessage.area) : ''}</FormHelperText>
                </FormControl>
                <FormControl error={!!validatedMessage['unit']}>
                    <BaseTextField
                        label={t('Unit')}
                        required={checkIsRequired('unit')}
                        error={!!validatedMessage.unit}
                        value={editData.unit || ''}
                        onChange={(e) => inputChangeHandler(e, 'unit')}
                    />
                    <FormHelperText>{validatedMessage.unit ? t(validatedMessage.unit) : ''}</FormHelperText>
                </FormControl>
                <FormControl error={!!validatedMessage['price']}>
                    <BaseTextField
                        label={t('Price')}
                        required={checkIsRequired('price')}
                        error={!!validatedMessage.price}
                        value={editData.price ?? ''}
                        onChange={(e) => inputChangeHandler(e, 'price')}
                    />
                    <FormHelperText>{validatedMessage.price ? t(validatedMessage.price) : ''}</FormHelperText>
                </FormControl>
                <FormControl error={!!validatedMessage['promo_price']}>
                    <BaseTextField
                        label={t('Promo Price')}
                        required={checkIsRequired('promo_price')}
                        error={!!validatedMessage.promo_price}
                        value={editData.promo_price ?? ''}
                        onChange={(e) => inputChangeHandler(e, 'promo_price')}
                    />
                    <FormHelperText>
                        {validatedMessage.promo_price ? t(validatedMessage.promo_price) : ''}
                    </FormHelperText>
                </FormControl>
                <FormControl error={!!validatedMessage['default_layout']}>
                    <BaseTextField
                        label={t('Default Layout')}
                        required={checkIsRequired('default_layout')}
                        error={!!validatedMessage.default_layout}
                        value={editData.default_layout ?? ''}
                        onChange={(e) => inputChangeHandler(e, 'default_layout')}
                    />
                    <FormHelperText>
                        {validatedMessage.default_layout ? t(validatedMessage.default_layout) : ''}
                    </FormHelperText>
                </FormControl>
                <FormControl error={!!validatedMessage['custom_fields']}>
                    <BaseTextField
                        label={t('Custom Fields')}
                        required={checkIsRequired('custom_fields')}
                        error={!!validatedMessage.custom_fields}
                        value={editData.custom_fields || ''}
                        onChange={(e) => inputChangeHandler(e, 'custom_fields')}
                    />
                    <FormHelperText>{validatedMessage.custom_fields ? t('Required') : ''}</FormHelperText>
                </FormControl>
            </div>
        </BaseDialog>
    );
}
